<template>
  <v-container>
    <v-dialog v-model="errorDialog.show" max-width="480">
      <message-dialog
        v-if="errorDialog.show"
        title="エラー"
        :message="errorDialog.message"
        @close="$router.push('/').catch(() => {})"
      ></message-dialog>
    </v-dialog>
    <v-dialog v-model="assignConfirmDialog.show" max-width="480">
      <confirm-dialog
        v-if="assignConfirmDialog.show"
        title="確認"
        :message="'患者 ' + assignConfirmDialog.patientName + ' に割り当てます。\nよろしいですか。'"
        okButtonLabel="はい"
        okButtonColor="red darken-1"
        @ok="assignMember"
        @cancel="$router.push('/').catch(() => {})"
      ></confirm-dialog>
    </v-dialog>
  </v-container>
</template>

<script>
import Confirm from "@/components/dialogs/Confirm";
import Message from "@/components/dialogs/Message";
import { MESSAGES } from "@/const/message";
import errorHandlerMixin from "@/mixins/errorHandlerMixin";
import { getAuth } from "firebase/auth";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import moment from "moment";
import { mapActions, mapState } from "vuex";

export default {
  name: "MemberAssign",
  components: { ConfirmDialog: Confirm, MessageDialog: Message },
  mixins: [errorHandlerMixin()],
  data: () => ({
    patient: {},
    code: {},

    // エラーダイアログ
    errorDialog: {
      show: false,
      message: "",
    },

    // 紐付け確認ダイアログ
    assignConfirmDialog: {
      show: false,
      patientName: "",
    },

    // 処理中フラグ
    isProcessing: false,
  }),
  computed: {
    // ログインユーザー
    ...mapState("user", ["selfUser"]),
  },
  created() {
    const token = this.$route.query.invitation_token;
    // 招待用のtokenが渡されていない場合はエラー
    if (!token) {
      this.errorDialog.message = "不正なURLです";
      this.errorDialog.show = true;
      return;
    }

    const auth = getAuth();
    auth.authStateReady().then(async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        // 未ログインの場合はtokenを保持したままログイン画面に遷移
        this.$router.push("/?invitation_token=" + token).catch(() => {});
        return;
      }

      // tokenから codes ドキュメントを取得
      const q = query(collection(this.$firestore, "codes"), where("code", "==", token));
      const codeSnapshot = await getDocs(q);

      // codesドキュメントが取得できない場合はエラー
      if (codeSnapshot.empty) {
        this.errorDialog.message = "不正なURLです";
        this.errorDialog.show = true;
        return;
      }

      this.code = { uid: codeSnapshot.docs[0].id, ...codeSnapshot.docs[0].data() };

      // 有効期限切れの場合エラー
      const now = moment();
      const created = moment(this.code.created.toDate());
      const expired = created.add(30, "minutes");
      if (now.isAfter(expired)) {
        this.errorDialog.message = "有効期限が切れています。\n再度招待を受けてください。";
        this.errorDialog.show = true;
        return;
      }

      // メールアドレスチェック
      // 招待メール送信先とログイン情報とでメールアドレスが異なっていたらエラー
      // QRコード招待の場合はチェックしない
      if (this.code.newEmail) {
        if (this.code.newEmail != this.selfUser.email) {
          this.errorDialog.message =
            "招待先のメールアドレスとログイン情報が異なっています。\n招待メールを受け取ったメールアドレスでログインし直し、再度招待URLを開いてください。";
          this.errorDialog.show = true;
          return;
        }
      }

      // codes から紐付け先の患者情報を取得
      const userUid = this.code.userUuid;
      const userSnapshot = await getDoc(doc(this.$firestore, "users", userUid));

      // 紐付け先の患者が存在しなければエラー
      if (!userSnapshot.exists()) {
        this.errorDialog.message = "対象の患者が存在しません。\n再度招待を受けてください。";
        this.errorDialog.show = true;
        return;
      }

      this.patient = { uid: userSnapshot.id, ...userSnapshot.data() };

      // コード使用済の場合
      if (this.code.used) {
        // 割り当て済み
        if (this.patient.memberUids.includes(this.$store.state.user.selfUser?.uid)) {
          this.errorDialog.message = "既に対象の患者に割り当てられています。";
          this.errorDialog.show = true;
          return;
        }
        // 未割り当て
        this.errorDialog.message =
          "この招待URLは既に使用されています。\n再度招待を受けてください。";
        this.errorDialog.show = true;
        return;
      }

      // 確認ダイアログを表示
      this.assignConfirmDialog.patientName = this.patient?.lastName + " " + this.patient?.firstName;
      this.assignConfirmDialog.show = true;
    });
  },
  methods: {
    ...mapActions("api", ["incrementRunningApiCount", "decrementRunningApiCount"]),

    // 患者に紐付けを行う
    async assignMember() {
      try {
        this.isProcessing = true;
        this.incrementRunningApiCount();

        // 紐付け先患者のuid
        const { uid } = this.patient;
        // 対象患者に紐づくメンバーのuid
        let memberUids = this.patient.memberUids ? this.patient.memberUids : [];
        memberUids.push(this.$store.state.user.selfUser?.uid);

        const codeUid = this.code.uid;

        const assignMembersToPatientFunction = this.$httpsCallable(
          this.$functions,
          "assignmemberstopatient"
        );
        await assignMembersToPatientFunction({ uid, memberUids, codeUid });

        this.$store.dispatch("snackbar/openSnackbar", {
          text: `メンバー割当を更新しました`,
          color: "success",
        });
        this.$router.push("/").catch(() => {});
      } catch (error) {
        this.showError(MESSAGES.ERRORS.UNEXPECTED);
      } finally {
        this.decrementRunningApiCount();
        this.isProcessing = false;
      }
    },
  },
};
</script>
